import React, { useState, useEffect } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { FileList } from '@/components/filemanagement/FileList';
import { FileComparisonView } from '@/components/filemanagement/FileComparisonView';
import { UploadDialog } from '@/components/filemanagement/UploadDialog';
import { IngestionProgressDialog } from '@/components/filemanagement/IngestionProgressDialog';
import { RebuildProgressDialog } from '@/components/filemanagement/RebuildProgressDialog';
import { useInputFiles } from '@/hooks/useFileManagement';
import { useDocumentStatus, useConsistencyCheck } from '@/hooks/useDocuments';
import { useAPIClient } from '@/hooks/useAPIClient';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Folder,
  AlertTriangle,
  FileText,
  CheckCircle,
  Clock,
  XCircle,
  Upload,
  Play,
  RotateCcw,
  Database,
  Loader2,
  Download,
  AlertCircle,
  CheckCircle2,
  RefreshCw
} from 'lucide-react';
import { InputFileInfo } from '@/types/api';

const REBUILD_TASK_KEY = 'active_rebuild_task';
const INGESTION_TASK_KEY = 'active_ingestion_task';

export default function FileManagement() {
  const [selectedFilepath, setSelectedFilepath] = useState<string | null>(null);
  const [isProcessingNew, setIsProcessingNew] = useState(false);
  const [isRetryingFailed, setIsRetryingFailed] = useState(false);
  const [isRebuildingDocs, setIsRebuildingDocs] = useState(false);
  const [showRebuildDialog, setShowRebuildDialog] = useState(false);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [showHealthDetails, setShowHealthDetails] = useState(false);
  const [activeIngestionTask, setActiveIngestionTask] = useState<{
    taskId: string;
    type: 'retry-failed' | 'process-new' | 'ingest';
  } | null>(null);
  const [activeRebuildTask, setActiveRebuildTask] = useState<{
    taskId: string;
    mode: 'all' | 'documents' | 'few-shot';
  } | null>(null);

  const {
    data: filesData,
    isLoading: filesLoading,
    error: filesError,
    refetch: refetchFiles
  } = useInputFiles();

  const { data: statusData, refetch: refetchStatus } = useDocumentStatus();
  const { data: healthData, refetch: checkHealth, isLoading: isCheckingHealth } = useConsistencyCheck();
  const apiClient = useAPIClient();
  const { toast } = useToast();
  const { isAdmin } = useAuth();

  // Restore active tasks from localStorage on mount
  useEffect(() => {
    const storedRebuildTask = localStorage.getItem(REBUILD_TASK_KEY);
    const storedIngestionTask = localStorage.getItem(INGESTION_TASK_KEY);

    if (storedRebuildTask) {
      try {
        const task = JSON.parse(storedRebuildTask);
        setActiveRebuildTask(task);
      } catch (e) {
        console.error('Failed to parse stored rebuild task:', e);
        localStorage.removeItem(REBUILD_TASK_KEY);
      }
    }

    if (storedIngestionTask) {
      try {
        const task = JSON.parse(storedIngestionTask);
        setActiveIngestionTask(task);
      } catch (e) {
        console.error('Failed to parse stored ingestion task:', e);
        localStorage.removeItem(INGESTION_TASK_KEY);
      }
    }
  }, []);

  // Persist rebuild task to localStorage when it changes
  useEffect(() => {
    if (activeRebuildTask) {
      localStorage.setItem(REBUILD_TASK_KEY, JSON.stringify(activeRebuildTask));
    } else {
      localStorage.removeItem(REBUILD_TASK_KEY);
    }
  }, [activeRebuildTask]);

  // Persist ingestion task to localStorage when it changes
  useEffect(() => {
    if (activeIngestionTask) {
      localStorage.setItem(INGESTION_TASK_KEY, JSON.stringify(activeIngestionTask));
    } else {
      localStorage.removeItem(INGESTION_TASK_KEY);
    }
  }, [activeIngestionTask]);

  const handleFileSelect = (filepath: string) => {
    setSelectedFilepath(filepath);
  };

  const handleBack = () => {
    setSelectedFilepath(null);
  };

  const handleProcessNew = async () => {
    setIsProcessingNew(true);
    try {
      const response = await apiClient.documents.processNew();
      console.log('[DEBUG] Process new response:', response);

      // Show progress dialog if we got a task_id
      if (response.task_id) {
        console.log('[DEBUG] Setting active task:', response.task_id);
        setActiveIngestionTask({
          taskId: response.task_id,
          type: 'process-new',
        });
      } else {
        console.log('[DEBUG] No task_id in response');
        // Fallback for immediate completion (no background task)
        toast({
          title: "Verarbeitung abgeschlossen",
          description: `${response.processed_count} Dateien verarbeitet`,
        });
        await Promise.all([refetchFiles(), refetchStatus()]);
      }
    } catch (error) {
      console.error('Process new failed:', error);
      toast({
        title: "Verarbeitung fehlgeschlagen",
        description: "Bitte erneut versuchen",
        variant: "destructive",
      });
    } finally {
      setIsProcessingNew(false);
    }
  };

  const handleRetryFailed = async () => {
    setIsRetryingFailed(true);
    try {
      const response = await apiClient.documents.retryFailed();

      // Show progress dialog if we got a task_id
      if (response.task_id) {
        setActiveIngestionTask({
          taskId: response.task_id,
          type: 'retry-failed',
        });
      } else {
        // Fallback for immediate completion (no background task)
        toast({
          title: "Wiederholung abgeschlossen",
          description: `${response.retried_count} Dateien verarbeitet`,
        });
        await Promise.all([refetchFiles(), refetchStatus()]);
      }
    } catch (error) {
      console.error('Retry failed:', error);
      toast({
        title: "Wiederholung fehlgeschlagen",
        description: "Bitte erneut versuchen",
        variant: "destructive",
      });
    } finally {
      setIsRetryingFailed(false);
    }
  };

  const handleRebuildDocsVdb = async () => {
    setShowRebuildDialog(false);
    setIsRebuildingDocs(true);
    try {
      const response = await apiClient.documents.rebuildVdb('documents');
      console.log('[DEBUG] Rebuild VDB response:', response);

      // Show progress dialog if we got a task_id
      if (response.task_id) {
        console.log('[DEBUG] Setting active rebuild task:', response.task_id);
        setActiveRebuildTask({
          taskId: response.task_id,
          mode: 'documents',
        });
      } else {
        // Fallback for immediate completion (shouldn't happen with new backend)
        toast({
          title: "VDB neu aufgebaut",
          description: "Dokumente wurden neu indiziert",
        });
        await Promise.all([refetchFiles(), refetchStatus()]);
      }
    } catch (error) {
      console.error('Rebuild VDB failed:', error);
      toast({
        title: "VDB Neuaufbau fehlgeschlagen",
        description: "Bitte erneut versuchen",
        variant: "destructive",
      });
    } finally {
      setIsRebuildingDocs(false);
    }
  };

  const handleDownloadAll = async () => {
    setIsDownloadingAll(true);
    try {
      const response = await apiClient.documents.downloadAll();

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="?(.+)"?/);
      const filename = filenameMatch ? filenameMatch[1] : 'processed_documents.zip';

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: "Download gestartet",
        description: "Alle .md Dateien und Metadaten werden heruntergeladen",
      });
    } catch (error) {
      console.error('Download all failed:', error);
      toast({
        title: "Download fehlgeschlagen",
        description: "Bitte erneut versuchen",
        variant: "destructive",
      });
    } finally {
      setIsDownloadingAll(false);
    }
  };

  const handleCheckHealth = async () => {
    await checkHealth();
    setShowHealthDetails(true);
  };

  // Find selected file object
  const selectedFile = selectedFilepath
    ? filesData?.files.find(f => f.filepath === selectedFilepath)
    : null;

  if (filesError) {
    return (
      <div className="w-full px-4 lg:px-6 py-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Failed to load files: {filesError.message}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {selectedFile ? (
        // File comparison view
        <FileComparisonView
          selectedFile={selectedFile}
          onBack={handleBack}
        />
      ) : (
        // File list view
        <div className="w-full px-4 lg:px-6 py-6 space-y-4 h-full flex flex-col">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-2">
                <Folder className="w-6 h-6" />
                Datei-Verwaltung
              </h1>
              <p className="text-muted-foreground mt-1">
                L&R verarbeitete Dokumentdateien
              </p>
            </div>

            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              {/* Rebuild VDB button */}
              <Button
                variant="outline"
                onClick={() => setShowRebuildDialog(true)}
                disabled={!isAdmin || isRebuildingDocs}
                title={!isAdmin ? "Nur für Administratoren" : undefined}
                size="sm"
                className="hidden sm:flex"
              >
                {isRebuildingDocs ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Neuaufbau...
                  </>
                ) : (
                  <>
                    <Database className="w-4 h-4 mr-2" />
                    VDB neu aufbauen
                  </>
                )}
              </Button>

              {/* Mobile version - icon only */}
              <Button
                variant="outline"
                onClick={() => setShowRebuildDialog(true)}
                disabled={!isAdmin || isRebuildingDocs}
                size="sm"
                className="sm:hidden"
                title={!isAdmin ? "Nur für Administratoren" : "VDB neu aufbauen"}
              >
                {isRebuildingDocs ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Database className="w-4 h-4" />
                )}
              </Button>

              {/* Process pending files button - more compact on small screens */}
              <Button
                variant="outline"
                onClick={handleProcessNew}
                disabled={!isAdmin || isProcessingNew || (statusData?.remaining || 0) === 0}
                title={!isAdmin ? "Nur für Administratoren" : undefined}
                size="sm"
                className="hidden sm:flex"
              >
                <Play className="w-4 h-4 mr-2" />
                {isProcessingNew ? 'Verarbeitung...' : 'Pending verarbeiten'}
              </Button>

              {/* Mobile version - icon only */}
              <Button
                variant="outline"
                onClick={handleProcessNew}
                disabled={!isAdmin || isProcessingNew || (statusData?.remaining || 0) === 0}
                size="sm"
                className="sm:hidden"
                title={!isAdmin ? "Nur für Administratoren" : isProcessingNew ? 'Verarbeitung...' : 'Pending verarbeiten'}
              >
                <Play className="w-4 h-4" />
              </Button>

              {/* Retry failed files button - compact on small screens */}
              {statusData && statusData.failed > 0 && (
                <>
                  <Button
                    variant="outline"
                    onClick={handleRetryFailed}
                    disabled={!isAdmin || isRetryingFailed}
                    title={!isAdmin ? "Nur für Administratoren" : undefined}
                    size="sm"
                    className="hidden sm:flex"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    {isRetryingFailed ? 'Wiederholung...' : 'Failed wiederholen'}
                  </Button>

                  {/* Mobile version - icon only */}
                  <Button
                    variant="outline"
                    onClick={handleRetryFailed}
                    disabled={!isAdmin || isRetryingFailed}
                    size="sm"
                    className="sm:hidden"
                    title={!isAdmin ? "Nur für Administratoren" : isRetryingFailed ? 'Wiederholung...' : 'Failed wiederholen'}
                  >
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                </>
              )}

              {/* Upload button - always visible, compact text on small screens */}
              <UploadDialog
                trigger={
                  <Button disabled={!isAdmin} title={!isAdmin ? "Nur für Administratoren" : undefined}>
                    <Upload className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Dokumente hochladen</span>
                    <span className="sm:hidden">Upload</span>
                  </Button>
                }
                onUploadComplete={async () => {
                  // Refresh the data without full page reload
                  await Promise.all([refetchFiles(), refetchStatus()]);
                }}
              />
            </div>
          </div>

          {/* Statistics Strip */}
          {filesData && !filesLoading && (
            <div className="bg-muted/30 rounded-lg px-4 py-3">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-6 flex-wrap">
                  <span className="text-sm font-medium text-muted-foreground">Übersicht:</span>
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-500" />
                    <span className="text-sm font-medium">{statusData?.total_documents || filesData.total}</span>
                    <span className="text-xs text-muted-foreground">total files</span>
                  </div>
                  {statusData?.unique_files && statusData.unique_files !== statusData.total_documents && (
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-muted-foreground">{statusData.unique_files}</span>
                      <span className="text-xs text-muted-foreground">unique</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm font-medium text-green-600">
                      {statusData?.already_processed || 0}
                    </span>
                    <span className="text-xs text-muted-foreground">processed</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-orange-500" />
                    <span className="text-sm font-medium text-orange-600">
                      {statusData?.remaining || 0}
                    </span>
                    <span className="text-xs text-muted-foreground">pending</span>
                  </div>
                  {statusData && statusData.failed > 0 && (
                    <div className="flex items-center gap-2">
                      <XCircle className="w-4 h-4 text-red-500" />
                      <span className="text-sm font-medium text-red-600">
                        {statusData.failed}
                      </span>
                      <span className="text-xs text-muted-foreground">failed</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Folder className="w-4 h-4 text-purple-500" />
                    <span className="text-sm font-medium">{Object.keys(filesData.modules).length}</span>
                    <span className="text-xs text-muted-foreground">modules</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Database className="w-4 h-4 text-blue-500" />
                    <span className="text-sm font-medium">
                      {statusData?.vdb_document_count || 0}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      VDB docs ({statusData?.vdb_chunk_count || 0} chunks)
                    </span>
                  </div>

                  {/* System Health - Admin only */}
                  {isAdmin && (
                    <div className="flex items-center gap-2">
                      {healthData?.status === 'healthy' ? (
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                      ) : healthData?.status === 'issues_found' ? (
                        <AlertCircle className="w-4 h-4 text-orange-500" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-gray-400" />
                      )}
                      <button
                        onClick={handleCheckHealth}
                        disabled={isCheckingHealth}
                        className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                        title="Check system health"
                      >
                        {isCheckingHealth ? 'Checking...' : 'System'}
                      </button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleCheckHealth}
                        disabled={isCheckingHealth}
                        className="h-6 w-6 p-0"
                      >
                        <RefreshCw className={`w-3 h-3 ${isCheckingHealth ? 'animate-spin' : ''}`} />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Download All button - right side of statistics */}
                <Button
                  variant="outline"
                  onClick={handleDownloadAll}
                  disabled={isDownloadingAll}
                  size="sm"
                >
                  {isDownloadingAll ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Download...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      <span className="hidden sm:inline">Alle .md herunterladen</span>
                      <span className="sm:hidden">Download</span>
                    </>
                  )}
                </Button>
              </div>
              
              {/* Modules breakdown */}
              <div className="mt-2 flex flex-wrap gap-2">
                {Object.entries(filesData.modules).map(([module, stats]) => (
                  <Badge key={module} variant="secondary" className="text-xs">
                    {module}: {stats.processed}/{stats.total}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className="flex-1 min-h-0">
            {filesLoading ? (
              <FileListSkeleton />
            ) : filesData ? (
              <FileList
                files={filesData.files}
                modules={filesData.modules}
                onFileSelect={handleFileSelect}
                selectedFile={selectedFilepath}
                onDataChange={async () => {
                  await Promise.all([refetchFiles(), refetchStatus()]);
                }}
              />
            ) : null}
          </div>
        </div>
      )}

      {/* Rebuild VDB Confirmation Dialog */}
      <AlertDialog open={showRebuildDialog} onOpenChange={setShowRebuildDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Dokumenten-VDB neu aufbauen?</AlertDialogTitle>
            <AlertDialogDescription>
              Dies wird die Vektordatenbank für alle Dokumente neu aufbauen. Der Vorgang kann einige Minuten dauern und während dieser Zeit ist die App für andere Nutzer nicht verwendbar. Möchten Sie fortfahren?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleRebuildDocsVdb}>
              Fortfahren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Health Check Details Dialog */}
      <AlertDialog open={showHealthDetails} onOpenChange={setShowHealthDetails}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {healthData?.status === 'healthy' ? '✅ System Healthy' : '⚠️ Consistency Issues Found'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              <div className="space-y-3">
                {/* File counts */}
                <div className="bg-muted/30 rounded-lg p-3 space-y-2">
                  <p className="text-sm font-medium">Output Directory:</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">.md files:</span>
                      <span className="ml-2 font-medium">{healthData?.total_md_files || 0}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">.metadata.json files:</span>
                      <span className="ml-2 font-medium">{healthData?.total_metadata_files || 0}</span>
                    </div>
                  </div>
                  {healthData?.total_md_files === healthData?.total_metadata_files ? (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      Counts match perfectly
                    </p>
                  ) : (
                    <p className="text-xs text-orange-600 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Count mismatch detected
                    </p>
                  )}
                </div>

                {healthData?.status === 'healthy' ? (
                  <p className="text-sm">All documents are consistent. No orphaned files detected.</p>
                ) : (
                  <div className="space-y-3">
                    <p className="font-medium">Found {healthData?.total_issues} issue(s):</p>

                  {healthData?.orphaned_metadata && healthData.orphaned_metadata.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-orange-600 mb-1">
                        Orphaned Metadata ({healthData.orphaned_metadata.length}):
                      </p>
                      <ul className="text-xs space-y-1 max-h-32 overflow-y-auto">
                        {healthData.orphaned_metadata.map((file, idx) => (
                          <li key={idx} className="font-mono">{file}</li>
                        ))}
                      </ul>
                      <p className="text-xs text-muted-foreground mt-2">
                        These metadata files have no corresponding .md file
                      </p>
                    </div>
                  )}

                  {healthData?.orphaned_md && healthData.orphaned_md.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-orange-600 mb-1">
                        Orphaned MD Files ({healthData.orphaned_md.length}):
                      </p>
                      <ul className="text-xs space-y-1 max-h-32 overflow-y-auto">
                        {healthData.orphaned_md.map((file, idx) => (
                          <li key={idx} className="font-mono">{file}</li>
                        ))}
                      </ul>
                      <p className="text-xs text-muted-foreground mt-2">
                        These .md files have no corresponding metadata file
                      </p>
                    </div>
                  )}
                  </div>
                )}
              </div>

              {healthData?.timestamp && (
                <p className="text-xs text-muted-foreground mt-4">
                  Last checked: {new Date(healthData.timestamp).toLocaleString()}
                </p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowHealthDetails(false)}>
              Close
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Ingestion Progress Dialog */}
      {(() => {
        console.log('[DEBUG] activeIngestionTask state:', activeIngestionTask);
        return activeIngestionTask ? (
          <IngestionProgressDialog
            taskId={activeIngestionTask.taskId}
            taskType={activeIngestionTask.type}
            onComplete={() => {
              // Refresh file lists when processing completes
              refetchFiles();
              refetchStatus();
            }}
            onClose={() => {
              setActiveIngestionTask(null);
            }}
          />
        ) : null;
      })()}

      {/* Rebuild Progress Dialog */}
      {(() => {
        console.log('[DEBUG] activeRebuildTask state:', activeRebuildTask);
        return activeRebuildTask ? (
          <RebuildProgressDialog
            taskId={activeRebuildTask.taskId}
            mode={activeRebuildTask.mode}
            onComplete={() => {
              // Refresh file lists when rebuild completes
              refetchFiles();
              refetchStatus();
            }}
            onClose={() => {
              setActiveRebuildTask(null);
            }}
          />
        ) : null;
      })()}
    </div>
  );
}

function FileListSkeleton() {
  return (
    <div className="space-y-4">
      {/* Search skeleton */}
      <div className="flex gap-4">
        <Skeleton className="h-10 flex-1" />
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-10 w-32" />
      </div>
      
      {/* Stats skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <Skeleton className="h-8 w-12 mb-2" />
                  <Skeleton className="h-4 w-20" />
                </div>
                <Skeleton className="w-8 h-8 rounded" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      {/* List skeleton */}
      <div className="space-y-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <Skeleton className="w-4 h-4 mt-1" />
                  <div className="flex-1 min-w-0">
                    <Skeleton className="h-5 w-3/4 mb-2" />
                    <div className="flex items-center gap-4">
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-4 w-12" />
                      <Skeleton className="h-4 w-20" />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Skeleton className="h-6 w-16" />
                  <Skeleton className="h-6 w-12" />
                  <Skeleton className="h-8 w-8" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}